/**
 * Sensitivity panel (FA pack — FA6).
 *
 * Renders:
 *   1. A ±deltaPct slider + a metric toggle (LCOE / IRR / NPV).
 *   2. A tornado chart — one bar per sensitive variable, sorted by
 *      swing-absolute. The bar straddles the metric base, drawn from
 *      `metricLow` (left) to `metricHigh` (right). Asymmetric swings
 *      land off-center.
 *   3. A small data table beneath the chart with the per-variable
 *      low / base / high readings so the user can grab numbers.
 *
 * Pure read on `FinancialAssumptions` — does not write back. The form
 * + recompute drive the canonical values; this panel just visualises
 * the per-variable swings.
 */
import React, { useMemo, useState } from 'react';
import { Slider } from '../../ui/slider';
import {
  SENSITIVITY_VARIABLES,
  computePhasedSensitivity,
  computeSensitivity,
  fieldLabel,
  formatMetric,
  type SensitivityMetric,
  type SensitivityResult,
  type PhasedSensitivityResult,
} from '../../../lib/financialSensitivity';
import type {
  FinancialAssumptions,
  FinancialAssumptionsCore,
} from '../../../types/financialAssumptions';

export interface SensitivityPanelProps {
  fa?: FinancialAssumptions | null;
  /** The financial assumptions core — usually `site.financialAssumptions`. */
  core: FinancialAssumptionsCore | null;
}

const METRIC_OPTIONS: Array<{ value: SensitivityMetric; label: string }> = [
  { value: 'lcoe', label: 'LCOE' },
  { value: 'irr',  label: 'IRR' },
  { value: 'npv',  label: 'NPV' },
];

export function SensitivityPanel({ fa, core }: SensitivityPanelProps): React.JSX.Element {
  const [metric, setMetric] = useState<SensitivityMetric>('lcoe');
  const [deltaPct, setDeltaPct] = useState(20);

  const result: SensitivityResult | null = useMemo(() => {
    if (!core) return null;
    if (fa && (fa.phases?.length ?? 0) > 1) {
      return computePhasedSensitivity(fa, metric, deltaPct);
    }
    return computeSensitivity(core, metric, deltaPct);
  }, [fa, core, metric, deltaPct]);

  const phased = fa && (fa.phases?.length ?? 0) > 1 && result != null && 'phaseRows' in result
    ? (result as PhasedSensitivityResult)
    : null;
  const phaseRows = phased?.phaseRows ?? [];

  if (!core) {
    return (
      <div className="border border-border bg-surface-1 rounded-lg p-4 text-[11px] text-text-muted">
        Enter financial assumptions above to see ±{deltaPct}% sensitivity.
      </div>
    );
  }

  const hasAnyRow = result?.rows.some(r => r.swingAbs != null) ?? false;

  return (
    <div className="border border-border bg-surface-1 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-text-primary">
            Sensitivity
          </div>
          {/* B10 — name the basis. `metricBase` is the WHOLE-PROJECT figure the
              driver rows were perturbed around; it is NOT a per-phase roll-up
              (that is reported separately, with its own basis, below). */}
          <div className="text-[10px] text-text-muted mt-0.5">
            ±{deltaPct}% on each driver — whole-project {metricLabel(metric)} base{' '}
            <strong>{formatMetric(metric, result?.metricBase ?? null)}</strong>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {METRIC_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setMetric(opt.value)}
              className={`px-2.5 py-1 type-meta border ${
                metric === opt.value
                  ? 'border-primary text-primary bg-primary/10'
                  : 'border-border text-text-secondary hover:bg-state-hover'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        <div className="grid grid-cols-[80px_1fr_auto] gap-3 items-center">
          <span className="type-meta text-text-secondary">
            Range
          </span>
          <Slider
            value={[deltaPct]}
            min={5}
            max={50}
            step={5}
            onValueChange={vs => setDeltaPct(vs[0] ?? 20)}
            aria-label="Sensitivity range"
          />
          <span className="text-[10px] text-text-secondary tabular-nums">
            ±{deltaPct}%
          </span>
        </div>

        {!hasAnyRow && (
          <div className="text-[11px] text-text-muted text-center px-6 py-6 border border-dashed border-border">
            Sensitivity requires CAPEX, capacity, capacity factor, WACC, and PPA
            price. Set them in the Financial Model inputs and the tornado will
            populate.
          </div>
        )}

        {hasAnyRow && result && <Tornado result={result} />}

        {phaseRows.length > 0 && result && phased && (
          <div className="text-[10px] text-text-muted mb-3 border border-border rounded-md overflow-hidden">
            <div className="px-2 py-1.5 border-b border-border text-text-secondary">
              <div className="type-section text-text-secondary">
                Per-phase — project {metricLabel(metric)}{' '}
                <strong className="tabular-nums">
                  {phased.blendKind === 'not-blendable'
                    ? '—'
                    : formatMetric(metric, phased.blendedMetricBase)}
                </strong>
              </div>
              <div className="mt-0.5 normal-case tracking-normal text-text-muted">
                {phased.blendBasis}
              </div>
            </div>
            {phaseRows.map(row => (
              <div
                key={row.phaseId}
                className="grid grid-cols-[1fr_auto_auto_auto] gap-2 px-2 py-1 border-b border-border last:border-b-0"
              >
                <span className="truncate">
                  {row.phaseLabel} ({row.weightMw} MW
                  {row.capexBasis === 'campus-allocated' ? ', CAPEX allocated' : ''})
                </span>
                <span className="text-right w-20 tabular-nums">{formatMetric(result.metric, row.metricLow)}</span>
                <span className="text-right w-20 tabular-nums text-text-secondary">{formatMetric(result.metric, row.metricBase)}</span>
                <span className="text-right w-20 tabular-nums">{formatMetric(result.metric, row.metricHigh)}</span>
              </div>
            ))}
          </div>
        )}

        {hasAnyRow && result && (
          <div className="text-[10px] text-text-muted">
            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 px-2 py-1.5 border-b border-border font-semibold uppercase tracking-widest">
              <span>Driver</span>
              <span className="text-right w-20">−{deltaPct}%</span>
              <span className="text-right w-20">Base</span>
              <span className="text-right w-20">+{deltaPct}%</span>
            </div>
            {result.rows.map(row => (
              <div
                key={row.field}
                className="grid grid-cols-[1fr_auto_auto_auto] gap-2 px-2 py-1 border-b border-border last:border-b-0"
              >
                <span className="truncate">{fieldLabel(row.field)}</span>
                <span className="text-right w-20 tabular-nums">
                  {formatMetric(result.metric, row.metricLow)}
                </span>
                <span className="text-right w-20 tabular-nums text-text-secondary">
                  {formatMetric(result.metric, row.metricBase)}
                </span>
                <span className="text-right w-20 tabular-nums">
                  {formatMetric(result.metric, row.metricHigh)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Tornado({ result }: { result: SensitivityResult }): React.JSX.Element {
  const rows = result.rows.filter(r => r.swingAbs != null && r.metricLow != null && r.metricHigh != null);
  if (rows.length === 0) return <></>;

  // Compute the chart's metric extent so every bar shares a consistent
  // x-axis. Pads ±5% so the largest swing doesn't touch the edges.
  let extentLo = Infinity;
  let extentHi = -Infinity;
  for (const r of rows) {
    if (r.metricLow != null) extentLo = Math.min(extentLo, r.metricLow);
    if (r.metricHigh != null) extentHi = Math.max(extentHi, r.metricHigh);
    if (r.metricBase != null) {
      extentLo = Math.min(extentLo, r.metricBase);
      extentHi = Math.max(extentHi, r.metricBase);
    }
  }
  if (!Number.isFinite(extentLo) || !Number.isFinite(extentHi)) return <></>;
  const span = Math.max(extentHi - extentLo, 1e-6);
  const pad = span * 0.05;
  const xLo = extentLo - pad;
  const xHi = extentHi + pad;
  const xRange = xHi - xLo;

  const toPct = (v: number): number => ((v - xLo) / xRange) * 100;

  const base = result.metricBase ?? 0.5 * (extentLo + extentHi);

  return (
    <div className="border border-border rounded-md overflow-hidden">
      <div className="px-3 py-2 border-b border-border bg-surface-2 flex items-center justify-between">
        <span className="type-meta text-text-secondary">
          {metricLabel(result.metric)} swing — ±{result.deltaPct}%
        </span>
        <span className="text-[10px] text-text-muted tabular-nums">
          {formatMetric(result.metric, base)} base
        </span>
      </div>
      <div className="relative px-3 py-3">
        {/* base line */}
        <div
          aria-hidden
          className="absolute top-3 bottom-3 w-px bg-text-muted/60"
          style={{ left: `calc(12px + ${toPct(base)}% * (100% - 24px) / 100)` }}
        />
        <div className="space-y-1.5">
          {rows.map(row => {
            const lo = row.metricLow as number;
            const hi = row.metricHigh as number;
            const left = Math.max(0, Math.min(toPct(lo), toPct(hi)));
            const right = Math.min(100, Math.max(toPct(lo), toPct(hi)));
            const width = Math.max(right - left, 1);
            return (
              <div key={row.field} className="grid grid-cols-[140px_1fr] gap-2 items-center">
                <span className="text-[10px] truncate text-text-secondary" title={fieldLabel(row.field)}>
                  {fieldLabel(row.field)}
                </span>
                <div className="relative h-3 bg-surface-2 border border-border overflow-hidden">
                  <div
                    className="absolute top-0 bottom-0 bg-primary/40 border border-primary"
                    style={{ left: `${left}%`, width: `${width}%` }}
                    title={`${fieldLabel(row.field)}: ${formatMetric(result.metric, lo)} → ${formatMetric(result.metric, hi)}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function metricLabel(metric: SensitivityMetric): string {
  switch (metric) {
    case 'lcoe': return 'LCOE';
    case 'irr':  return 'IRR';
    case 'npv':  return 'NPV';
  }
}
